import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment-timezone';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Button, message, Typography, Grid, Space, Card, Layout, Flex } from 'antd';
import { UserAddOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { getUsers, createUser, updateUser, deleteUser, getUserToken, getProducts, deleteUserProfilePicture } from './UsersServices';
import UserTable from './UserTable/UserTable';
import UserModal from './UserModal/UserModal';
import UserViewModal from './UserViewModal/UserViewModal';
import { format } from 'date-fns';
import { Input } from 'antd';
import { debounce } from 'lodash';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import { saveAuthDataForAdmin } from '../../Config/ApiConfig';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { resolveBackendMessage } from '../../Utils/apiMessages';
import SharedFilterBar from '../Shared/Filters/SharedFilterBar';
import { userFilterFields } from '../Shared/Filters/filterConfig';

dayjs.extend(utc);

const { Title } = Typography;
const { useBreakpoint } = Grid;

const UserList = () => {
    const { changeUser } = useGlobal();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const { handleError } = useErrorHandler();
    const [modalVisible, setModalVisible] = useState(false);
    const [viewModalVisible, setViewModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [viewingUser, setViewingUser] = useState(null);
    const [searchString, setSearchString] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sort_by, setSortBy] = useState(undefined);
    const [sort_direction, setSortDirection] = useState(undefined);
    const [chipFilters, setChipFilters] = useState([]);
    const [normalizedChipFilters, setNormalizedChipFilters] = useState({});
    const [pagination, setPagination] = useState({
        total: 0,
        last: false,
    });

    const screens = useBreakpoint();

    const fetchUsers = useCallback(async (search_string) => {
        try {
            setLoading(true); // This loading state now triggers skeleton loading in UserTable
            const response = await getUsers({
                page,
                size: pageSize,
                sort_by,
                sort_direction,
                search_string,
                filters: normalizedChipFilters || {},
            });
            setUsers(
                response.data.map(user => ({ 
                    ...user, 
                    discount_expiry: user.discount_expiry ? dayjs(user.discount_expiry) : null 
                }))
            );
            setPagination({
                total: response.metadata.total,
                last: response.metadata.last,
            });
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    }, [setLoading, handleError, page, pageSize, sort_by, sort_direction, normalizedChipFilters]);

    const getUserTokenCall = useCallback(async (id) => {
        setLoading(true);
        try {
            const { data } = await getUserToken(id);
            saveAuthDataForAdmin(data);
            changeUser(data.user);
            if (!data.user?.is_admin) {
                navigate('/');
            }
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    }, [setLoading, handleError, changeUser, navigate]);

    const handleSearch = (e) => {
        setSearchString(e.target.value);
        setPage(1);
        debouncedFetchUsers(e.target.value);
    };

    const debouncedFetchUsers = useCallback(
        debounce((search) => {
            fetchUsers(search);
        }, 500),
        [fetchUsers]
    );

    useEffect(() => {
        fetchUsers(searchString);
    }, [fetchUsers, searchString]);

    const handleTableChange = (pagination, _tableFilters, sorter) => {
        const { order, field } = sorter || {};
        let sort_by = field;
        if (field === 'role') {
            sort_by = 'is_admin';
        }
        const sort_direction = order === 'ascend' ? 'asc' : order === 'descend' ? 'desc' : undefined;
        if (pagination.pageSize) {
            setPageSize(pagination.pageSize);
        }
        
        // Reset sort state when order is undefined (sorting is removed)
        if (order === undefined) {
            setSortBy(undefined);
            setSortDirection(undefined);
        } else if (sort_by) {
            setSortBy(sort_by);
            
            if (sort_direction) {
                setSortDirection(sort_direction);
            }
        }
        if (pagination.current) {
            setPage(pagination.current);
        }
    };

    const handleChipSortChange = useCallback(({ sort_by: nextSortBy, sort_direction: nextSortDirection }) => {
        setSortBy(nextSortBy);
        setSortDirection(nextSortDirection);
        setPage(1);
    }, []);

    const showModal = (user = null) => {
        setEditingUser(user);
        setModalVisible(true);
    };

    const handleCancel = () => {
        setModalVisible(false);
        setEditingUser(null);
    };

    const showViewModal = (user = null) => {
        setViewingUser(user);
        setViewModalVisible(true);
    };

    const handleViewCancel = () => {
        setViewModalVisible(false);
        setViewingUser(null);
    };

    const handleFormSubmit = async (values) => {
        try {
            const defaultValues = {
                ...values,
                discount_percentage: values.discount_percentage
                    ? parseFloat(values.discount_percentage)
                    : undefined,
                discount_expiry: values.discount_expiry
                    ? dayjs(values.discount_expiry).utc().format('YYYY-MM-DDTHH:mm:ss+00:00')
                    : undefined,
            }
            let updatedValues = {
                ...defaultValues,
            };
            if (defaultValues.timezone) {
                const timezone = defaultValues.timezone;
                const offset = moment.tz(timezone).utcOffset() / 60;
                updatedValues = {
                    ...defaultValues,
                    timezone: offset,
                    timezone_name: timezone,
                };
            }
            const filteredValues = Object.fromEntries(
                Object.entries(updatedValues).filter(([_, v]) => v != null)
            );
            if (editingUser) {
                const payload = await updateUser(editingUser.id, filteredValues);
                const text = resolveBackendMessage(t, payload);
                if (text) message.success(text);
            } else {
                const payload = await createUser(filteredValues);
                const text = resolveBackendMessage(t, payload);
                if (text) message.success(text);
            }
            handleCancel();
            fetchUsers(searchString);
        } catch (error) {
            handleError(error);
        }
    };

    const handleDelete = async (id) => {
        setLoading(true);
        try {
            const payload = await deleteUser(id);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            fetchUsers(searchString);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteProfilePicture = async (id) => {
        setLoading(true);
        try {
            const payload = await deleteUserProfilePicture(id);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            fetchUsers(searchString);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };


    return (
        <Layout>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Card
                    style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}
                    size='medium'
                    title={t('user_management')}
                    extra={
                        <Button
                            type="link"
                            icon={<PlusOutlined />}
                            size="medium"
                            onClick={() => showModal()}
                        >
                            {!screens.xs && t('add_user')}
                        </Button>
                    }
                    styles={{
                        body: {
                            padding: 16
                        }
                    }}
                >
                    <Flex
                        gap="middle"
                        wrap="wrap"
                        align="center"
                        justify="space-between"
                        style={{ width: '100%' }}
                    >
                        <Flex flex={{ xs: '100%', md: 1 }} style={{ flexGrow: 1 }}>
                            <Input
                                prefix={<SearchOutlined />}
                                placeholder={t('search_users')}
                                value={searchString}
                                onChange={handleSearch}
                                onFocus={() => setIsInputFocused(true)}
                                onBlur={() => setIsInputFocused(false)}
                                style={{
                                    width: isInputFocused ? '100%' : (screens.xs ? '100%' : '300px'),
                                    transition: 'width 0.4s ease-in-out'
                                }}
                            />
                        </Flex>

                        <SharedFilterBar
                            fields={userFilterFields}
                            value={chipFilters}
                            onChange={(nextFilters, normalized) => {
                                setChipFilters(nextFilters);
                                setNormalizedChipFilters(normalized);
                                setPage(1);
                                // fetchUsers will be triggered by useEffect via updated dependencies
                            }}
                            sortFields={[
                                { value: 'id', labelKey: 'id' },
                                { value: 'email', labelKey: 'email' },
                                { value: 'is_admin', labelKey: 'role' },
                                { value: 'is_verified', labelKey: 'verified' },
                                { value: 'is_disabled', labelKey: 'filters_field_disabled' },
                                { value: 'created_at', labelKey: 'created_at' },
                                { value: 'updated_at', labelKey: 'updated_at' },
                            ]}
                            sortValue={{ sort_by, sort_direction }}
                            onSortChange={handleChipSortChange}
                        />
                    </Flex>
                </Card>
                <Card style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                    <UserTable
                        users={users}
                        loading={loading}
                        pagination={{
                            ...pagination,
                            current: page,
                            pageSize: pageSize,
                        }}
                        screens={screens}
                        handleTableChange={handleTableChange}
                        showModal={showModal}
                        handleDelete={handleDelete}
                        showViewModal={showViewModal}
                        getUserTokenCall={getUserTokenCall}
                        setLoading={setLoading}
                        searchString={searchString}
                    />
                </Card>
                <UserModal
                    modalVisible={modalVisible}
                    handleCancel={handleCancel}
                    handleSubmit={handleFormSubmit}
                    editingUser={editingUser}
                    screens={screens}
                    setLoading={setLoading}
                />
                <UserViewModal
                    modalVisible={viewModalVisible}
                    handleCancel={handleViewCancel}
                    user={viewingUser}
                    screens={screens}
                    onDeleteProfilePicture={handleDeleteProfilePicture}
                />
            </Space>
        </Layout>
    );
};

export default UserList;
